import { Suspense, useEffect, useMemo, useRef } from "react"
import { useFrame } from "@react-three/fiber"
import { Environment, MeshReflectorMaterial, useGLTF } from "@react-three/drei"
import * as THREE from "three"

import { MeshErrorBoundary } from "../Avatar/MeshErrorBoundary"

// 2026-05-05 v3 — Inyección de la GLB del Domo 1.0 hosteada en R2.
// La estructura procedural (costillas + ventanal + anillo basal) deja
// de generarse cuando USE_GLB_SHELL es true; se reemplaza por el .glb
// firmado por Zak'Haar en Blender. Mantenemos:
//   · El pad central (3 anillos cyan) — interactivo, marca el punto
//     de descenso del Avatar y tiene animación que el .glb no replica.
//   · El Environment HDRI — para reflejos físicamente correctos sobre
//     el biopolímero del Domo y sobre las mallas de los avatares.
//   · La iluminación volumétrica.
// Si el .glb falla (404, CORS, archivo corrupto), el MeshErrorBoundary
// captura el throw y rendereamos la versión procedural como fallback
// silencioso. La URL del .glb se reemplaza acá cuando Zak suba una
// nueva iteración (?v= invalida el cache de useGLTF y de R2).
const USE_GLB_SHELL = true
// 2026-05-06 — Domo_3.3: rim del Dome_Shell pegado al piso (Z=0)
// + reescalado uniforme a radio 28 para que la pared coincida con
// Floor_White. Antes la pared se extendía a radio 37.81 y se veía
// "gigantesca" envolviendo más allá del piso reflector. Cuando se
// suba el GLB a R2, el path queda directo apuntando a esta versión.
const DOMO_GLB_URL =
    "https://pub-94bd1d71bb304c91ad7b8e146063f337.r2.dev/Domo%20Cero/Estructuras/Domos/Domo_3.3.glb"

// Pre-cargamos la GLB al montar el módulo (los Tripulantes que llegan
// a la app pasan por el orbe del Estado Cero antes de descender al
// Domo — ese rito de ~5s da margen para que la GLB esté lista cuando
// la fase pase a "descending").
useGLTF.preload(DOMO_GLB_URL)

interface DomoGltfMeshProps {
    opacity: number
}

// Componente interno que carga + clona la GLB del Domo y aplica
// opacity / depth de manera consistente con el resto del entorno
// (fade-in con `intensity` global).
//
// 2026-05-06 — Reverted a la lógica simple original. La capa de
// transparencia "para ver estrellas a través de la pared" generaba
// artefactos de renderizado (rectángulo negro al iniciar enlaces,
// pared del fondo invisible cuando la cámara salía del Domo). El
// Tripulante ya pidió volver al Domo opaco y limpio. Si más
// adelante queremos cielo estrellado visible desde dentro, la vía
// correcta es por la GLB (un techo con transmission o ventanal
// abierto) o por un material custom del techo, no aplicando
// transparencia uniforme a todas las superficies.
function DomoGltfMesh({ opacity }: DomoGltfMeshProps) {
    const gltf = useGLTF(DOMO_GLB_URL)
    // Clonamos para que múltiples montajes no compartan materiales
    // mutables; la GLB del Domo es una sola instancia visual pero
    // remountar (HMR, navegación interna) sin clone produce drift.
    const cloned = useMemo(() => gltf.scene.clone(true), [gltf.scene])

    useEffect(() => {
        const op = Math.max(0, Math.min(1, opacity))
        cloned.traverse((obj: THREE.Object3D) => {
            const mesh = obj as THREE.Mesh
            if (!mesh.isMesh) return
            // 2026-05-06 — DoubleSide solo al Dome_Shell (la pared/techo
            // estructural). Antes se forzaba a TODOS los materiales,
            // pero eso multiplicaba el coste de render del piso, los
            // LEDs y los Mezz — combinado con el reflector + el bloom
            // saturaba la GPU en escritorio (errores
            // GL_INVALID_OPERATION: vertex buffer not big enough).
            // El Dome_Shell es donde realmente importa el DoubleSide
            // (cámara dentro mira al interior, normal apunta afuera);
            // los demás meshes están bien con FrontSide por default.
            const isWall = mesh.name === "Dome_Shell"
            const mats = Array.isArray(mesh.material)
                ? mesh.material
                : [mesh.material]
            for (const m of mats) {
                const std = m as THREE.MeshStandardMaterial
                if ("transparent" in std) {
                    std.transparent = op < 1
                    std.opacity = op
                    std.depthWrite = op > 0.5
                    if (isWall) std.side = THREE.DoubleSide
                    std.needsUpdate = true
                }
            }
        })
    }, [cloned, opacity])

    return <primitive object={cloned} />
}

// Sprint 4 v2 — DomoCeroEnvironment (rediseño 2026-05-05)
//
// Templo orgánico estilo Zaha Hadid flotando sobre un suelo de espejo
// cuántico. La meta visual: arquitectura solarpunk, biopolímero blanco
// inmaculado, ventanal panorámico al espacio profundo, nervaduras
// curvas entrelazadas con tiras LED bioluminiscentes.
//
// Capas (de adentro hacia afuera, todas opacas con `intensity` global):
//   1. Suelo reflector — MeshReflectorMaterial con resolución alta y
//      blur reducido para que parezca un espejo cuántico real.
//   2. Pad central — anillos concéntricos cyan emisivos pulsando.
//   3. Costillas blancas — CatmullRomCurve3 que arrancan desde el piso
//      a un radio externo, cruzan por arriba y bajan al lado opuesto.
//      Materializadas con tubeGeometry y meshPhysicalMaterial blanco
//      con clearcoat alto. Geometría más orgánica que un torus simple.
//   4. Tiras LED — tubo delgado paralelo a cada costilla, alternando
//      cyan y dorado. Material emisivo para captar el bloom.
//   5. Ventanal panorámico — sección curva transparente en la mitad
//      trasera. Las estrellas de fondo (definidas en Canvas.tsx) se
//      ven a través naturalmente porque el material es transmissive.
//   6. Anillo basal continuo que une las costillas al piso.
//   7. Environment HDRI — preset "studio" para reflejos prístinos
//      sobre la malla 3D del avatar y el biopolímero. Solo desktop
//      (mobile depende del bloom + lights).
//
// Performance:
//   · mobile: 5 costillas, reflector 768x, blur ligero, sin Environment
//   · desktop: 7 costillas, reflector 1024x, blur 0, Environment studio
//
// El parámetro `intensity` (0..1) hace fade-in suave de toda la
// arquitectura cuando el Tripulante pasa de void a descending.

interface Props {
    intensity?: number
    isMobile?: boolean
}

const RIB_COUNT_DESKTOP = 7
const RIB_COUNT_MOBILE = 5
const FLOOR_RADIUS = 18
const APEX_HEIGHT = 16
const RIB_INNER_RADIUS = 16

// Genera una curva orgánica para una costilla del domo. La costilla
// nace en el suelo a un ángulo `angle` (en el círculo de radio R), sube
// con un ligero ofset hacia el centro creando una curva tipo "lágrima
// invertida" elongada, alcanza el ápice cerca del centro y baja al
// lado opuesto rotado 180°. Resultado: arco no semicircular sino
// alargado y elegante (firma Zaha Hadid).
function buildRibCurve(angle: number): THREE.CatmullRomCurve3 {
    const baseA = new THREE.Vector3(
        Math.cos(angle) * FLOOR_RADIUS,
        0,
        Math.sin(angle) * FLOOR_RADIUS
    )
    const liftA = new THREE.Vector3(
        Math.cos(angle) * (RIB_INNER_RADIUS - 1),
        APEX_HEIGHT * 0.55,
        Math.sin(angle) * (RIB_INNER_RADIUS - 1)
    )
    const apex = new THREE.Vector3(0, APEX_HEIGHT, 0)
    const liftB = new THREE.Vector3(
        Math.cos(angle + Math.PI) * (RIB_INNER_RADIUS - 1),
        APEX_HEIGHT * 0.55,
        Math.sin(angle + Math.PI) * (RIB_INNER_RADIUS - 1)
    )
    const baseB = new THREE.Vector3(
        Math.cos(angle + Math.PI) * FLOOR_RADIUS,
        0,
        Math.sin(angle + Math.PI) * FLOOR_RADIUS
    )
    const curve = new THREE.CatmullRomCurve3(
        [baseA, liftA, apex, liftB, baseB],
        false,
        "catmullrom",
        0.4
    )
    return curve
}

export function DomoCeroEnvironment({
    intensity = 1,
    isMobile = false,
}: Props) {
    const padRingRef = useRef<THREE.Mesh>(null)
    const padInnerRef = useRef<THREE.Mesh>(null)
    const padCoreRef = useRef<THREE.Mesh>(null)

    const op = Math.max(0, Math.min(1, intensity))
    const ribCount = isMobile ? RIB_COUNT_MOBILE : RIB_COUNT_DESKTOP

    // Pre-construir las geometrías de costillas (white) + LED (color
    // alternado). Cada costilla es 2 tubos co-curvados: uno blanco
    // grueso, uno emisivo finito al costado.
    const ribs = useMemo(() => {
        const out: {
            white: THREE.TubeGeometry
            led: THREE.TubeGeometry
            ledColor: string
            key: number
        }[] = []
        for (let i = 0; i < ribCount; i++) {
            // Distribución medio-circular: las costillas se cruzan por
            // arriba pero arrancan en diferentes puntos del círculo
            // del piso. Ángulo solo en la mitad para no duplicar
            // geometría (cada curva ya recorre los dos extremos).
            const angle = (i / ribCount) * Math.PI
            const curve = buildRibCurve(angle)
            const white = new THREE.TubeGeometry(
                curve,
                isMobile ? 80 : 140,
                0.32, // radio del tubo blanco
                10,
                false
            )
            const led = new THREE.TubeGeometry(
                curve,
                isMobile ? 80 : 140,
                0.07, // radio del LED finito
                6,
                false
            )
            // Alternar color de LED — cyan (firma Domo) y dorado (sello
            // RSV). 60/40 a favor de cyan para mantener el frío del
            // espacio con acentos cálidos.
            const ledColor = i % 2 === 0 ? "#00E5FF" : "#FFD27A"
            out.push({ white, led, ledColor, key: i })
        }
        return out
    }, [ribCount, isMobile])

    // Cleanup de geometrías al cambiar el set (dispone si se monta una
    // versión con distinto count o se desmonta el componente).
    useEffect(() => {
        return () => {
            ribs.forEach((r) => {
                r.white.dispose()
                r.led.dispose()
            })
        }
    }, [ribs])

    useFrame((state) => {
        const t = state.clock.elapsedTime
        if (padRingRef.current) {
            const s = 1 + Math.sin(t * 1.3) * 0.045
            padRingRef.current.scale.set(s, s, 1)
        }
        if (padInnerRef.current) {
            padInnerRef.current.rotation.z += 0.0025
            const s = 1 + Math.sin(t * 1.7 + 1.2) * 0.06
            padInnerRef.current.scale.set(s, s, 1)
        }
        if (padCoreRef.current) {
            const mat = padCoreRef.current.material as THREE.MeshStandardMaterial
            mat.emissiveIntensity = (1.6 + Math.sin(t * 2.2) * 0.6) * op
        }
    })

    // 2026-05-05 v3 — Cuando USE_GLB_SHELL está activo, la arquitectura
    // del Domo (suelo + costillas + ventanal + anillo basal) viene del
    // .glb firmado por Zak'Haar en Blender. El pad central, el
    // Environment HDRI y las luces se pintan SIEMPRE — el .glb no las
    // replica porque son interactivas / dinámicas. Si la GLB falla, el
    // MeshErrorBoundary muestra la versión procedural como fallback
    // silencioso (no rompe el Canvas).
    const proceduralShell = (
        <>
            {/* ── 1. SUELO ESPEJO CUÁNTICO ─────────────────────────── */}
            <mesh
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0, 0]}
                receiveShadow
            >
                <circleGeometry args={[80, 96]} />
                <MeshReflectorMaterial
                    blur={isMobile ? [220, 90] : [140, 60]}
                    resolution={isMobile ? 512 : 768}
                    mixBlur={isMobile ? 1 : 0.95}
                    mixStrength={isMobile ? 22 : 40}
                    roughness={0.72}
                    depthScale={1.1}
                    minDepthThreshold={0.4}
                    maxDepthThreshold={1.4}
                    color="#080c14"
                    metalness={0.55}
                    transparent
                    opacity={op}
                    mirror={0}
                />
            </mesh>

            {/* ── 3 + 4. COSTILLAS BLANCAS + TIRAS LED ───────────────
                Cada par (white + led) usa la misma curva CatmullRom
                pero con radios distintos para que el LED quede como
                una "venadura" emisiva pegada a la costilla blanca. */}
            {ribs.map(({ white, led, ledColor, key }) => (
                <group key={key}>
                    <mesh
                        geometry={white}
                        castShadow
                        receiveShadow
                    >
                        <meshPhysicalMaterial
                            color="#fafafa"
                            roughness={0.18}
                            metalness={0.05}
                            clearcoat={1.0}
                            clearcoatRoughness={0.08}
                            sheen={0.4}
                            sheenColor="#cdf1ff"
                            transparent
                            opacity={op}
                        />
                    </mesh>
                    <mesh geometry={led}>
                        <meshStandardMaterial
                            color="#000"
                            emissive={ledColor}
                            emissiveIntensity={1.1 * op}
                            transparent
                            opacity={op}
                        />
                    </mesh>
                </group>
            ))}

            {/* ── 5. VENTANAL PANORÁMICO ─────────────────────────────
                Mitad trasera de una hemisfera transparente. El front es
                el espacio "abierto" donde están los avatares y el pad;
                la mitad trasera (z < 0) es cristal cuántico fino que
                deja ver las estrellas y la nebulosa. side BackSide para
                que la cara interior se ilumine con los reflejos. */}
            <mesh position={[0, 0, 0]}>
                <sphereGeometry
                    args={[
                        18.6,
                        72,
                        48,
                        // theta: solo la mitad trasera (de PI a 2*PI)
                        Math.PI,
                        Math.PI,
                        0,
                        Math.PI / 2.05,
                    ]}
                />
                <meshPhysicalMaterial
                    color="#A8DAFF"
                    roughness={0.04}
                    metalness={0}
                    transmission={isMobile ? 0.7 : 0.94}
                    thickness={isMobile ? 0.6 : 1.6}
                    ior={1.45}
                    clearcoat={1}
                    clearcoatRoughness={0.1}
                    side={THREE.BackSide}
                    transparent
                    opacity={Math.max(0.16, op * 0.72)}
                />
            </mesh>

            {/* Marco LED del ventanal — un toro fino que recorre el
                borde superior de la mitad trasera. Acento dorado para
                contrastar con el cyan dominante. */}
            <mesh
                position={[0, 0, 0]}
                rotation={[Math.PI / 2, 0, 0]}
            >
                <torusGeometry
                    args={[18.55, 0.06, 8, 96, Math.PI]}
                />
                <meshStandardMaterial
                    color="#000"
                    emissive="#FFD27A"
                    emissiveIntensity={0.8 * op}
                    transparent
                    opacity={op}
                />
            </mesh>

            {/* Borde inferior del ventanal — anillo cyan que recorre
                el horizonte de la mitad trasera, anclando visualmente
                el ventanal al suelo espejo. */}
            <mesh
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0.04, 0]}
            >
                <ringGeometry
                    args={[18.4, 18.65, 96, 1, Math.PI, Math.PI]}
                />
                <meshStandardMaterial
                    color="#000"
                    emissive="#00E5FF"
                    emissiveIntensity={0.7 * op}
                    transparent
                    opacity={op}
                    side={THREE.DoubleSide}
                />
            </mesh>

            {/* ── 6. ANILLO BASAL CONTINUO ───────────────────────────
                Une las costillas al piso refortando la sensación de
                templo cerrado. Capa adicional bajo los pies del Avatar. */}
            <mesh
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0.06, 0]}
            >
                <ringGeometry args={[17.8, 18.2, 128]} />
                <meshPhysicalMaterial
                    color="#fafafa"
                    roughness={0.15}
                    metalness={0.05}
                    clearcoat={1}
                    transparent
                    opacity={op}
                    side={THREE.DoubleSide}
                />
            </mesh>
        </>
    )

    return (
        <group>
            {/* Shell del Domo: GLB de Blender (firma de Zak'Haar) o
                versión procedural si la GLB no carga / no está
                habilitada. El MeshErrorBoundary captura el throw que
                useGLTF lanza cuando R2 falla y renderea la procedural
                como fallback silencioso.
                2026-05-06 — Suspense `fallback={proceduralShell}` (era
                `fallback={null}`). Cuando un re-suspend del useGLTF
                ocurría durante un cambio de fase del enlace (mount
                de LinkBeam dispara HMR/reconcile del subtree), el
                fallback null dejaba el Domo invisible un frame
                completo — la pantalla quedaba en negro hasta que
                el GLB volvía a estar listo. Con la procedural como
                fallback la pared y el piso siempre tienen algo que
                pintar y el negro desaparece. La procedural
                comparte radio (18 m) con el Dome_Shell así no se
                nota el cambio cuando reaparece el GLB. */}
            {USE_GLB_SHELL ? (
                <Suspense fallback={proceduralShell}>
                    <MeshErrorBoundary
                        label="Domo Cero shell"
                        fallback={proceduralShell}
                    >
                        <DomoGltfMesh opacity={op} />
                    </MeshErrorBoundary>
                </Suspense>
            ) : (
                proceduralShell
            )}

            {/* ── PAD CENTRAL — ANILLOS CYAN EMISIVOS ────────────────
                SIEMPRE renderizado. Tres capas concéntricas pulsando
                a frecuencias distintas. El más grande late al ritmo
                del Avatar, el medio gira lento y respira, el núcleo
                emite el bloom más intenso. La GLB del Domo no replica
                este pad porque es interactivo (punto de descenso de
                los Avatares) y tiene useFrame propio. */}
            <mesh
                ref={padRingRef}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0.005, 0]}
            >
                <ringGeometry args={[1.5, 1.7, 96]} />
                <meshStandardMaterial
                    color="#000"
                    emissive="#00FFFF"
                    emissiveIntensity={1.2 * op}
                    transparent
                    opacity={op}
                    side={THREE.DoubleSide}
                />
            </mesh>
            <mesh
                ref={padInnerRef}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0.006, 0]}
            >
                <ringGeometry args={[0.9, 1.05, 96]} />
                <meshStandardMaterial
                    color="#000"
                    emissive="#7FF6FF"
                    emissiveIntensity={0.85 * op}
                    transparent
                    opacity={op}
                    side={THREE.DoubleSide}
                />
            </mesh>
            <mesh
                ref={padCoreRef}
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, 0.007, 0]}
            >
                <circleGeometry args={[0.6, 64]} />
                <meshStandardMaterial
                    color="#000"
                    emissive="#7FF6FF"
                    emissiveIntensity={0.7 * op}
                    transparent
                    opacity={0.6 * op}
                />
            </mesh>

            {/* ── ENVIRONMENT HDRI ───────────────────────────────────
                Solo desktop. El preset "city" da reflejos cinemáticos
                equilibrados. environmentIntensity acota la contribución
                global. Mobile lo desactiva — el bloom + lights +
                reflector entregan suficiente profundidad sin pagar el
                costo del HDRI. Mounted early, carga durante el Estado
                Cero. */}
            {!isMobile && (
                <Environment
                    preset="city"
                    background={false}
                    environmentIntensity={0.35}
                />
            )}

            {/* ── ILUMINACIÓN VOLUMÉTRICA DEL DOMO ─────────────────── */}
            <pointLight
                position={[0, 0.4, 0]}
                intensity={1.4 * op}
                distance={isMobile ? 16 : 22}
                color="#00FFFF"
                decay={1.6}
            />
            <pointLight
                position={[0, APEX_HEIGHT - 1, 0]}
                intensity={0.7 * op}
                distance={isMobile ? 16 : 24}
                color="#FFD27A"
                decay={1.5}
            />
            <pointLight
                position={[0, 6, -16]}
                intensity={0.4 * op}
                distance={28}
                color="#7FB8FF"
                decay={1.6}
            />
        </group>
    )
}
